import { useRef, useState } from "react";
import { FileUp, Sparkles } from "lucide-react";
import { GlassCard } from "@/components/common/GlassCard";
import { InstructionWordLimit } from "@/components/form/InstructionWordLimit";
import { Button } from "@/components/ui/button";
import { ConflictResolutionPanel } from "@/components/draft/ConflictResolutionPanel";
import { PartyConfirmationPanel } from "./PartyConfirmationPanel";
import { SourceDocumentsUploader } from "./SourceDocumentsUploader";
import type { DraftFactConflict, DraftWizardData } from "@/types/drafting";

interface Step2BackgroundPartiesProps {
  data: DraftWizardData;
  conflicts: DraftFactConflict[];
  allPartiesConfirmed: boolean;
  onChange: <K extends keyof DraftWizardData>(field: K, value: DraftWizardData[K]) => void;
  onExtractFromPaste: (text: string) => Promise<void>;
  onExtractFromFile: (text: string) => Promise<void>;
  onConfirmParties: () => Promise<void>;
  onResolveConflict: (conflictId: string, value: string) => Promise<void>;
  isExtracting?: boolean;
  isConfirming?: boolean;
  isResolvingConflict?: boolean;
}

export function Step2BackgroundParties({
  data,
  conflicts,
  allPartiesConfirmed,
  onChange,
  onExtractFromPaste,
  onExtractFromFile,
  onConfirmParties,
  onResolveConflict,
  isExtracting = false,
  isConfirming = false,
  isResolvingConflict = false,
}: Step2BackgroundPartiesProps) {
  const [partyPaste, setPartyPaste] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handlePartyFile(files: FileList | null) {
    if (!files?.[0]) return;
    const file = files[0];
    const text = await file.text().catch(() => "");
    if (text.trim()) {
      await onExtractFromFile(text);
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <GlassCard className="border-[var(--lm-border-card)] bg-white dark:border-glass-border dark:bg-slate-950/45">
        <h2 className="mb-1 text-base font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
          Background
        </h2>
        <p className="mb-4 text-xs text-[var(--lm-muted)] dark:text-slate-400">
          Provide deal context, commercial terms, and any instructions for the drafting counsel.
        </p>
        <textarea
          value={data.backgroundInfo}
          onChange={(e) => onChange("backgroundInfo", e.target.value)}
          rows={10}
          className="mb-4 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          placeholder="Describe the transaction, key commercial terms, and any special instructions..."
        />
        <textarea
          value={data.agentInstructions}
          onChange={(e) => onChange("agentInstructions", e.target.value)}
          rows={4}
          className="mb-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          placeholder="Optional additional instructions for the drafting counsel"
        />
        <div className="mb-4">
          <InstructionWordLimit
            text={data.agentInstructions}
            kind="draft-additional"
            onSummarized={(summary) => onChange("agentInstructions", summary)}
          />
        </div>
        <SourceDocumentsUploader
          documents={data.sourceDocuments.filter((d) => d.docType === "background" || d.docType === "party-source")}
          onChange={(docs) => {
            const other = data.sourceDocuments.filter(
              (d) => d.docType !== "background" && d.docType !== "party-source"
            );
            onChange("sourceDocuments", [...other, ...docs]);
          }}
        />
      </GlassCard>

      <GlassCard className="border-[var(--lm-border-card)] bg-white dark:border-glass-border dark:bg-slate-950/45">
        <h2 className="mb-1 text-base font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
          Parties
        </h2>
        <p className="mb-4 text-xs text-[var(--lm-muted)] dark:text-slate-400">
          Paste party details or attach a party-source document, then confirm before generating. You
          can edit names after confirming — click Save party changes to update the matter.
        </p>

        <textarea
          value={partyPaste}
          onChange={(e) => setPartyPaste(e.target.value)}
          rows={5}
          className="mb-3 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          placeholder="Paste Party A and Party B details..."
        />

        <div className="mb-4 flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!partyPaste.trim() || isExtracting}
            onClick={() => onExtractFromPaste(partyPaste)}
            className="gap-2"
          >
            <Sparkles className="h-4 w-4" />
            {isExtracting ? "Extracting…" : "Extract from paste"}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isExtracting}
            onClick={() => fileInputRef.current?.click()}
            className="gap-2"
          >
            <FileUp className="h-4 w-4" />
            {isExtracting ? "Extracting…" : "Attach & extract"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.docx,.txt"
            onChange={(e) => {
              void handlePartyFile(e.target.files);
              e.target.value = "";
            }}
          />
        </div>

        <ConflictResolutionPanel
          conflicts={conflicts}
          onResolve={onResolveConflict}
          isResolving={isResolvingConflict}
        />

        <PartyConfirmationPanel
          parties={data.parties}
          onChange={(parties) => onChange("parties", parties)}
          onConfirm={() => onConfirmParties()}
          isConfirming={isConfirming}
          allConfirmed={allPartiesConfirmed}
        />
      </GlassCard>
    </div>
  );
}
